import React, { useState, useEffect, useMemo } from 'react';
import {
	Dialog, DialogTitle, DialogContent, DialogActions,
	Button, CircularProgress, Typography, Box,
	FormGroup, FormControlLabel, Checkbox, Radio, RadioGroup,
	TextField, IconButton,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Paper
} from '@mui/material';
// The ClearIcon is still used for the cancel button.
import ClearIcon from '@mui/icons-material/Clear';
// The CheckIcon is no longer needed as the confirmation is automatic.
// import CheckIcon from '@mui/icons-material/Check';
import { useGetOpportunityLostReasonHierarchyQuery, useMarkOpportunityAsLostMutation } from '../../../../features/api/opportunity-api-slice';
import { useGetUserDetailsHooks } from '../../../hooks';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

// Interfaces remain the same
interface Level3 {
	name: string;
	reason: string;
}
interface Level2 extends Level3 {
	type?: 'Check Box' | 'Radio Button';
	level_3?: Level3[];
}
interface Level1 extends Level2 {
	level_2?: Level2[];
}
interface LostReasonCategory {
	name: string;
	reason: string;
	level_1?: Level1[];
}
interface OthersInputState {
	key: string | null;
	value: string;
	groupKey?: string;
}
interface LostDetailsProps {
	opportunityId: string;
	open: boolean;
	onClose: () => void;
}

const Lostdetails: React.FC<LostDetailsProps> = ({ opportunityId, open, onClose }) => {
	const [reasonData, setReasonData] = useState<LostReasonCategory[]>([]);
	const [selections, setSelections] = useState<Record<string, any>>({});
	const [othersInputState, setOthersInputState] = useState<OthersInputState>({ key: null, value: '' });
	const { getUserToken } = useGetUserDetailsHooks();
	const { data, error, isLoading: isHierarchyLoading, isSuccess } =
		useGetOpportunityLostReasonHierarchyQuery({ userToken: getUserToken() });
	const [markOpportunityAsLost, { isLoading: isMarkingAsLostisLoading }] = useMarkOpportunityAsLostMutation();

	useEffect(() => {
		if (isSuccess && data?.message) {
			console.log("Received lost reason hierarchy:", data.message);
			setReasonData(data.message);
		}
	}, [isSuccess, data]);

	const resetState = () => {
		setSelections({});
		setOthersInputState({ key: null, value: '' });
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleSelectionChange = (
		key: string,
		value: string,
		isOther: boolean,
		isRadio: boolean,
		groupKey?: string
	) => {
		if (isOther) {
			setOthersInputState({
				key,
				value: typeof selections[key] === 'string' ? selections[key] : '',
				groupKey
			});
		}
		setSelections(prev => {
			const newSelections = { ...prev };
			if (isRadio && groupKey) {
				newSelections[groupKey] = key;
			} else {
				newSelections[key] = !prev[key];
				if (isOther && !newSelections[key]) {
					delete newSelections[key];
					if (othersInputState.key === key) {
						setOthersInputState({ key: null, value: '' });
					}
				}
			}
			return newSelections;
		});
	};

	// The handleOthersConfirm function is no longer needed.
	// const handleOthersConfirm = () => { ... };

	// MODIFICATION: The debounced function is updated to automatically save the "Other" reason text.
	// It now directly updates the main `selections` state after the user stops typing.
	const debouncedSaveOthersValue = useMemo(
		() =>
			debounce((key: string, value: string) => {
				setSelections(prev => ({
					...prev,
					[key]: value,
				}));
			}, 500), // 500ms debounce delay
		[]
	);

	useEffect(() => {
		return () => {
			// Cancel any pending debounced updates when the component unmounts.
			debouncedSaveOthersValue.cancel();
		};
	}, [debouncedSaveOthersValue]);


	const handleOthersCancel = () => {
		if (!othersInputState.key) return;
		if (othersInputState.groupKey) {
			setSelections(prev => {
				const newSelections = { ...prev };
				if (newSelections[othersInputState.groupKey as string] === othersInputState.key) {
					delete newSelections[othersInputState.groupKey as string];
				}
				// Also remove the specific text entry for the "Other" option
				delete newSelections[othersInputState.key as string];
				return newSelections;
			});
		} else {
			// Handle checkbox cancellation
			setSelections(prev => {
				const newSelections = { ...prev };
				delete newSelections[othersInputState.key as string];
				return newSelections;
			});
		}
		setOthersInputState({ key: null, value: '' });
	};

	// The buildLostReasonOutput and handleSave functions remain the same.
	const buildLostReasonOutput = () => {
		const output: any[] = [];
		reasonData.forEach((category) => {
			const categoryName = category.reason;
			category.level_1?.forEach((level1) => {
				const level1Name = level1.reason;
				const level1Key = `${level1.name}`;
				const level2Items = level1.level_2 || [];
				const level1Type = level1.type || 'Check Box';
				const isLevel1Radio = level1Type.toLowerCase().includes('radio');
				const level1Selected = isLevel1Radio
					? selections[level1Key] && selections[level1Key].startsWith(level1Key)
					: selections[level1Key];
				if (level2Items.length === 0 && level1Selected) {
					const isOther = typeof selections[level1Key] === 'string';
					output.push({
						lost_reason_category: categoryName,
						level_1: `${level1Name}-${categoryName}`,
						level_2: '',
						level_3: '',
						other_reason: isOther ? selections[level1Key] : ''
					});
				}

				level2Items.forEach((level2) => {
					const level2Name = level2.reason;
					const level2Key = `${level1.name}-${level2.name}`;
					const level3Items = level2.level_3 || [];

					const level2Type = level2.type || 'Check Box';
					const isLevel2Radio = level2Type.toLowerCase().includes('radio');

					const level2Selected = isLevel2Radio
						? selections[level1.name] === level2Key
						: selections[level2Key];

					if (level3Items.length === 0 && level2Selected) {
						const isOther = typeof selections[level2Key] === 'string';
						output.push({
							lost_reason_category: categoryName,
							level_1: `${level1Name}-${categoryName}`,
							level_2: `${level2Name}-${level1Name}-${categoryName}`,
							level_3: '',
							other_reason: isOther ? selections[level2Key] : ''
						});
					}

					level3Items.forEach((level3) => {
						const level3Name = level3.reason;
						const level3Key = `${level1.name}-${level2.name}-${level3.name}`;
						const groupKey = `${level1.name}-${level2.name}`;
						const isRadio = level2.type?.toLowerCase().includes('radio');

						const level3Selected = isRadio
							? selections[groupKey] === level3Key
							: selections[level3Key];

						if (level3Selected) {
							const isOther = typeof selections[level3Key] === 'string';
							output.push({
								lost_reason_category: categoryName,
								level_1: `${level1Name}-${categoryName}`,
								level_2: `${level2Name}-${level1Name}-${categoryName}`,
								level_3: `${level3Name}-${level2Name}-${level1Name}-${categoryName}`,
								other_reason: isOther ? selections[level3Key] : ''
							});
						}
					});
				});
			});
		});
		return output;
	};

	const selectedReasonList = useMemo(() => buildLostReasonOutput(), [selections, reasonData]); // Added reasonData dependency


	const handleSave = async () => {
		const finalData = buildLostReasonOutput();
		const payload = {
			opportunity_id: opportunityId,
			lost_reason: finalData,
		};
		try {
			const res = await markOpportunityAsLost({
				userToken: getUserToken(),
				body: payload,
			}).unwrap();
			console.log("Successfully marked as lost:", res);

			if (res.status_code === 200) {
				console.log(res)
				resetState();
				handleClose();
				toast.success(res.message)
			} else {
				toast.error(res.message);
			}
		} catch (err) {
			console.error("Failed to mark opportunity as lost:", err);
		}
	}

	const renderLevel = (items: (Level2 | Level3)[], parentKey: string, typeOverride?: 'Check Box' | 'Radio Button') => {
		if (!items || items.length === 0) return null;

		const inputType = typeOverride || (items[0] as Level2).type || 'Check Box';
		const isRadioGroup = inputType.toLowerCase().includes('radio');
		const groupKey = isRadioGroup ? parentKey : undefined;

		const content = items.map((item) => {
			const key = `${parentKey}-${item.name}`;
			const isOther = item.reason.toLowerCase().includes('other');
			const isSelected = isRadioGroup ? selections[groupKey as string] === key : !!selections[key];
			const hasSubLevels = 'level_3' in item && Array.isArray(item.level_3) && item.level_3.length > 0;

			return (
				<Box key={key}>
					{hasSubLevels ? (
						<Typography variant="subtitle2" sx={{ fontWeight: 600, my: 1 }}>
							{item.reason}
						</Typography>
					) : (
						<FormControlLabel
							control={
								isRadioGroup ? (
									<Radio
										checked={isSelected}
										onChange={() => handleSelectionChange(key, item.reason, isOther, true, groupKey)}
										size="small"
									/>
								) : (
									<Checkbox
										checked={isSelected}
										onChange={() => handleSelectionChange(key, item.reason, isOther, false)}
										size="small"
									/>
								)
							}
							label={<Typography variant="body2">{item.reason}</Typography>}
							sx={{ my: -0.95 }}
						/>
					)}

					{/* MODIFICATION: The text field appears if the "Other" option is selected. */}
					{/* The confirm button has been removed and the onChange now calls the debounced save function. */}
					{isOther && isSelected && (
						<Box display="flex" alignItems="center" pl={2} mb={1}>
							<TextField
								autoFocus
								fullWidth
								multiline
								size="small"
								variant="outlined"
								placeholder="Please specify"
								// The default value now comes directly from the main `selections` state.
								defaultValue={typeof selections[key] === 'string' ? selections[key] : ''}
								onChange={(e) => debouncedSaveOthersValue(key, e.target.value)}
							/>
							{/* REMOVED: The confirm button is no longer necessary. */}
							{/* <IconButton color="primary" onClick={handleOthersConfirm}><CheckIcon /></IconButton> */}
							<IconButton color="secondary" onClick={handleOthersCancel}><ClearIcon /></IconButton>
						</Box>
					)}
					{hasSubLevels && (
						<Box ml={3} mt={0.5}>
							{renderLevel((item as Level2).level_3!, key, (item as Level2).type)}
						</Box>
					)}
				</Box>
			);
		});

		return isRadioGroup ? (
			<RadioGroup name={groupKey} value={selections[groupKey as string] || null}>
				{content}
			</RadioGroup>
		) : (
			<FormGroup>{content}</FormGroup>
		);
	};

	// The rest of the component's return statement remains the same.
	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
				<DialogTitle>Select Lost Reason</DialogTitle>
				<DialogContent dividers sx={{ overflow: 'scroll', backgroundColor: '#f9f9f9' }}>
					{isHierarchyLoading && (
						<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
					)}
					{error && (
						<Typography color="error" textAlign="center">Error fetching data.</Typography>
					)}
					{!isHierarchyLoading && !error && reasonData?.length > 0 && (
						<Box
							sx={{
								columnCount: { xs: 1, sm: 2, md: 4, lg: 5 },
								columnGap: '16px',
								height: '110vh',
								paddingBottom: 1,
							}}
						>
							{reasonData.map((category) => (
								<Box
									key={category.name}
									sx={{
										breakInside: 'avoid',
										border: 1,
										borderColor: '#e0e0e0',
										borderRadius: 2,
										p: 1.25,
										bgcolor: 'white',
										mb: 2,
									}}
								>
									<Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem" gutterBottom>
										{category.reason}
									</Typography>

									{category.level_1?.map((level1) => (
										<Box key={level1.name} mb={2}>
											<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
												{level1.reason}
											</Typography>
											<Box pl={1}>
												{renderLevel(level1.level_2 || [], level1.name, level1.type)}
											</Box>
										</Box>
									))}
								</Box>
							))}
						</Box>
					)}
					{!isHierarchyLoading && !error && reasonData?.length === 0 && (
						<Typography textAlign="center" py={3}>
							No lost reason hierarchy available.
						</Typography>
					)}

					{selectedReasonList.length > 0 && (
						<Box mt={4}>
							<Typography variant="h6" gutterBottom>
								Selected Lost Reasons
							</Typography>
							<TableContainer component={Paper}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell><b>Category</b></TableCell>
											<TableCell><b>Level 1</b></TableCell>
											<TableCell><b>Level 2</b></TableCell>
											<TableCell><b>Level 3</b></TableCell>
											<TableCell><b>Other Reason</b></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{selectedReasonList.map((item, index) => (
											<TableRow key={index}>
												<TableCell>{item.lost_reason_category}</TableCell>
												<TableCell>{item.level_1}</TableCell>
												<TableCell>{item.level_2}</TableCell>
												<TableCell>{item.level_3}</TableCell>
												<TableCell>{item.other_reason || '-'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}

				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} variant="outlined">Close</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={Object.keys(selections).length === 0 || isMarkingAsLostisLoading}
					>
						{isMarkingAsLostisLoading ? <CircularProgress size={24} /> : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default Lostdetails;